import React, { useState, useRef } from 'react';
import { auth } from '../firebaseConfig';
import { useProfile } from '../context/ProfileContext';
import {
  createProfile,
  renameProfile,
  updateProfilePicture,
  FREE_PROFILE_LIMIT,
  MAX_PROFILE_PICTURE_BYTES
} from '../services/profilesService';
import { changeEmail, changeUsername } from '../services/accountService';
import IconButton from './IconButton';
import InitialAvatar from './InitialAvatar';
import Modal from './Modal';
import DeleteAccount from './DeleteAccount';
import './comic.css';

/**
 * Compact header control: shows the active Hero, opens a list of the Manager's
 * non-archived profiles, and offers "Add Profile" (subject to the free-tier
 * cap). Switching Heroes is always allowed, including mid-mission -- each
 * profile's Mission (if any) is independent (SessionContext keys sessions by
 * profileId), so switching focus here never ends or interrupts anyone's
 * in-progress workout, it just changes which Hero's view is showing.
 */
// Small caption label used above the account username / email / Hero name
// fields in the Edit Hero modal -- added so those three otherwise-identical
// dark input boxes are distinguishable once filled in (a plain input's
// placeholder text disappears once it has a value, so without a label a
// Hero literally named after the account's own username, e.g. "Flex Luthor"
// for both, is indistinguishable at a glance).
const fieldLabelStyle = {
  fontFamily: "'Anonymous Pro', monospace",
  fontWeight: "bold",
  fontSize: "13px",
  color: "#232526",
  marginBottom: "4px"
};

const ProfileSwitcher = ({ onUsernameChanged }) => {
  const { profiles, activeProfile, selectProfile, refreshProfiles } = useProfile();

  const [open, setOpen] = useState(false);
  const [adding, setAdding] = useState(false);
  const [name, setName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // Inline "Edit Hero" form state (rename + swap photo), keyed by profile id.
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState("");
  const [editFile, setEditFile] = useState(null);
  const [editPreviewUrl, setEditPreviewUrl] = useState(null);
  const [editSubmitting, setEditSubmitting] = useState(false);
  const [editError, setEditError] = useState(null);
  const editFileInputRef = useRef(null);

  // Inline "change login email" form, nested inside the Edit Hero modal --
  // account-level (Firebase Auth), not Hero-level, but the user wanted it
  // shown right there. Kept independent of the editName/editFile state above
  // since it has its own submit action and password field.
  const [editingEmail, setEditingEmail] = useState(false);
  const [newEmail, setNewEmail] = useState("");
  const [emailPassword, setEmailPassword] = useState("");
  const [emailSubmitting, setEmailSubmitting] = useState(false);
  const [emailError, setEmailError] = useState(null);
  const [emailSent, setEmailSent] = useState(false);

  const activeProfiles = profiles.filter((p) => p.isActive !== false);
  const atCap = activeProfiles.length >= FREE_PROFILE_LIMIT;
  // The oldest non-archived profile is the account's "main" one -- the same
  // implicit convention ProfileContext's resolveActiveProfile already uses
  // as the default-selected profile (selectable[0], list is oldest-first).
  // Its name IS the account username (kept in sync on save), so it gets a
  // single shared "Main Character Name" field instead of a separate
  // Username + Hero Name pair.
  const mainProfileId = activeProfiles[0]?.id;

  const closeMenu = () => {
    setOpen(false);
    setAdding(false);
    setName("");
    setError(null);
    cancelEdit();
  };

  const startEdit = (p) => {
    setAdding(false);
    setEditingId(p.id);
    setEditName(p.name);
    setEditFile(null);
    setEditPreviewUrl(null);
    setEditError(null);
    cancelEmailEdit();
  };

  const cancelEdit = () => {
    if (editPreviewUrl) URL.revokeObjectURL(editPreviewUrl);
    setEditingId(null);
    setEditName("");
    setEditFile(null);
    setEditPreviewUrl(null);
    setEditError(null);
    setEditSubmitting(false);
    if (editFileInputRef.current) editFileInputRef.current.value = "";
    cancelEmailEdit();
  };

  const startEmailEdit = () => {
    setEditingEmail(true);
    setNewEmail("");
    setEmailPassword("");
    setEmailError(null);
    setEmailSent(false);
  };

  const cancelEmailEdit = () => {
    setEditingEmail(false);
    setNewEmail("");
    setEmailPassword("");
    setEmailError(null);
    setEmailSent(false);
    setEmailSubmitting(false);
  };

  const handleEmailChangeSubmit = async () => {
    const trimmed = newEmail.trim();
    if (!trimmed || !emailPassword || emailSubmitting) return;

    setEmailSubmitting(true);
    setEmailError(null);
    const result = await changeEmail(trimmed, emailPassword);
    setEmailSubmitting(false);

    if (result.success) {
      setEmailSent(true);
      setEmailPassword("");
    } else {
      setEmailError(result.error || "Couldn't change your email. Try again.");
    }
  };

  const handleEditFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      setEditError("Please choose an image file.");
      return;
    }
    if (file.size > MAX_PROFILE_PICTURE_BYTES) {
      setEditError("That image is too large (5MB max).");
      return;
    }

    if (editPreviewUrl) URL.revokeObjectURL(editPreviewUrl);
    setEditError(null);
    setEditFile(file);
    setEditPreviewUrl(URL.createObjectURL(file));
  };

  const handleEditSave = async (e) => {
    e.preventDefault();
    if (editSubmitting) return;

    const trimmed = editName.trim();
    if (!trimmed) {
      setEditError("Give this profile a name.");
      return;
    }

    setEditSubmitting(true);
    setEditError(null);

    const target = profiles.find((p) => p.id === editingId);
    const isMain = target?.id === mainProfileId;
    let ok = true;

    // The main profile's name IS the account username -- keep them in sync
    // on every save rather than tracking them as two separate values.
    if (isMain && trimmed !== (auth.currentUser?.displayName || "")) {
      const result = await changeUsername(trimmed);
      if (!result.success) {
        ok = false;
        setEditError(result.error || "Couldn't update your username. Try again.");
      } else {
        onUsernameChanged?.(trimmed);
      }
    }

    if (ok && target && trimmed !== target.name) {
      const result = await renameProfile(editingId, trimmed);
      if (!result.success) {
        ok = false;
        setEditError("Couldn't rename your Hero. Try again.");
      }
    }

    if (ok && editFile && target) {
      const result = await updateProfilePicture(
        editingId,
        target.ownerUserId,
        editFile,
        target.photoPath
      );
      if (!result.success) {
        ok = false;
        setEditError("Couldn't upload that picture. Try again.");
      }
    }

    if (ok) {
      await refreshProfiles();
      cancelEdit();
    }
    setEditSubmitting(false);
  };

  const handleSelect = (profileId) => {
    if (profileId === activeProfile?.id) {
      closeMenu();
      return;
    }
    selectProfile(profileId);
    closeMenu();
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    const trimmed = name.trim();
    if (!trimmed || submitting) return;

    setSubmitting(true);
    setError(null);
    const result = await createProfile(activeProfile?.ownerUserId, trimmed);
    if (result.success) {
      setName("");
      setAdding(false);
      await refreshProfiles();
    } else {
      // Surface the real service error (incl. limitReached) rather than
      // trusting the UI-level disable to have caught it.
      setError(
        typeof result.error === "string"
          ? result.error
          : "Couldn't recruit your Hero. Try again."
      );
    }
    setSubmitting(false);
  };

  if (!activeProfile) return null;

  return (
    <div className="comic-panel" style={{ position: "relative", margin: "0 0 10px 0" }} data-tour="hero-switcher">
      <button
        onClick={() => (open ? closeMenu() : setOpen(true))}
        style={{
          display: "flex",
          alignItems: "center",
          gap: "10px",
          width: "100%",
          padding: "8px 12px",
          background: "none",
          border: "none",
          cursor: "pointer",
          textAlign: "left"
        }}
      >
        <InitialAvatar name={activeProfile.name} photoURL={activeProfile.photoURL} />
        <span
          style={{
            fontFamily: "'Anonymous Pro', monospace",
            color: "#232526",
            fontWeight: "bold",
            fontSize: "16px",
            flex: 1,
            minWidth: 0,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap"
          }}
        >
          {activeProfile.name}
        </span>
        <span className="comic-eyebrow" style={{ margin: 0 }}>
          {activeProfiles.length} / {FREE_PROFILE_LIMIT} Heroes
        </span>
      </button>

      {open && (
        <div style={{ padding: "0 8px 8px 8px" }}>
          {activeProfiles.map((p) => (
            <div
              key={p.id}
              className="comic-panel comic-panel--entry"
              style={{
                display: "flex",
                flexDirection: "column",
                width: "100%",
                margin: "0 0 8px 0",
                padding: "8px 10px"
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                <span style={{ width: "24px", flexShrink: 0, display: "flex", justifyContent: "center" }}>
                  {p.id === activeProfile.id && (
                    <span className="stamp-row__badge stamp-row__badge--check">&#10003;</span>
                  )}
                </span>
                <button
                  onClick={() => handleSelect(p.id)}
                  className="comic-tap"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "10px",
                    flex: 1,
                    minWidth: 0,
                    padding: 0,
                    border: "none",
                    background: "none",
                    cursor: "pointer",
                    textAlign: "left"
                  }}
                >
                  <InitialAvatar name={p.name} photoURL={p.photoURL} size={28} />
                  <span
                    style={{
                      fontFamily: "'Anonymous Pro', monospace",
                      color: "#232526",
                      fontSize: "15px",
                      flex: 1,
                      minWidth: 0,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap"
                    }}
                  >
                    {p.name}
                  </span>
                </button>
                <IconButton
                  icon={editingId === p.id ? "close" : "edit"}
                  onClick={() => (editingId === p.id ? cancelEdit() : startEdit(p))}
                  color={editingId === p.id ? "#fd874e" : "#232526"}
                  label={editingId === p.id ? `Cancel editing ${p.name}` : `Edit ${p.name}`}
                />
              </div>

              {editingId === p.id && (() => {
                const isMain = p.id === mainProfileId;
                return (
                <Modal title={`Edit ${p.name}`} onClose={cancelEdit}>
                <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "8px" }}>
                  <InitialAvatar
                    name={editName || p.name}
                    photoURL={editPreviewUrl || p.photoURL}
                    size={44}
                  />
                  <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                    <IconButton
                      icon="swap"
                      onClick={() => editFileInputRef.current?.click()}
                      color="#232526"
                      label="Change photo"
                    />
                    <input
                      ref={editFileInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleEditFileChange}
                      style={{ display: "none" }}
                    />
                  </div>
                </div>

                {/* Account-level login email -- not Hero-scoped, so it's only
                    shown on the main profile's edit modal (per direct user
                    request: secondary Heroes don't own the account, so its
                    email has no business appearing on their edit screen).
                    Also doubles as the Contact form's Reply-To, since
                    Contact.jsx reads it live off auth.currentUser at submit
                    time. Styled to match the dark .comic-input fields rather
                    than the white .comic-eyebrow chip, per direct user
                    request. */}
                {isMain && (
                <div style={{ marginBottom: "12px", paddingBottom: "12px", borderBottom: "2px solid #232526" }}>
                  <div style={fieldLabelStyle}>Main Account Email</div>
                  <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                    <span
                      className="comic-input"
                      style={{
                        display: "flex",
                        alignItems: "center",
                        flex: 1,
                        minWidth: 0,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap"
                      }}
                    >
                      {auth.currentUser?.email}
                    </span>
                    <IconButton
                      icon={editingEmail ? "close" : "edit"}
                      onClick={() => (editingEmail ? cancelEmailEdit() : startEmailEdit())}
                      color={editingEmail ? "#fd874e" : "#232526"}
                      label={editingEmail ? "Cancel changing email" : "Change email"}
                    />
                  </div>

                  {editingEmail && (
                    emailSent ? (
                      <p className="comic-empty" style={{ margin: "8px 0 0 0" }}>
                        Check {newEmail} for a confirmation link to finish changing your email.
                      </p>
                    ) : (
                      <div style={{ marginTop: "8px" }}>
                        <input
                          type="email"
                          placeholder="New email"
                          value={newEmail}
                          onChange={(e) => setNewEmail(e.target.value)}
                          className="comic-input"
                          style={{ marginBottom: "8px" }}
                        />
                        <input
                          type="password"
                          placeholder="Current password"
                          value={emailPassword}
                          onChange={(e) => setEmailPassword(e.target.value)}
                          className="comic-input"
                          style={{ marginBottom: "8px" }}
                        />
                        <button
                          type="button"
                          onClick={handleEmailChangeSubmit}
                          disabled={emailSubmitting || !newEmail.trim() || !emailPassword}
                          className="comic-btn comic-btn--go"
                        >
                          {emailSubmitting ? "Sending..." : "Send Confirmation"}
                        </button>
                        {emailError && <div className="comic-alert-bubble">{emailError}</div>}
                      </div>
                    )
                  )}
                </div>
                )}

                <form onSubmit={handleEditSave}>
                  <div style={fieldLabelStyle}>{isMain ? "Main Character Name" : "Name"}</div>
                  <input
                    type="text"
                    placeholder={isMain ? "Main Character Name" : "Name"}
                    value={editName}
                    onChange={(e) => setEditName(e.target.value)}
                    className="comic-input"
                    style={{ marginBottom: "8px" }}
                  />

                  <div style={{ display: "flex", gap: "8px" }}>
                    <button
                      type="submit"
                      disabled={editSubmitting || !editName.trim()}
                      className="comic-btn comic-btn--go"
                    >
                      {editSubmitting ? "Saving..." : "Save"}
                    </button>
                    <button
                      type="button"
                      onClick={cancelEdit}
                      className="comic-btn comic-btn--end"
                      style={{ height: "46px", marginTop: "10px", fontSize: "17px" }}
                    >
                      Cancel
                    </button>
                  </div>
                  {editError && <div className="comic-alert-bubble">{editError}</div>}
                </form>
                </Modal>
                );
              })()}
            </div>
          ))}

          {adding ? (
            <form onSubmit={handleAdd} style={{ marginTop: "4px" }}>
              <div style={{ display: "flex", gap: "8px" }}>
                <input
                  type="text"
                  placeholder="New Hero name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="comic-input"
                  autoFocus
                />
              </div>
              <button
                type="submit"
                disabled={submitting || !name.trim()}
                className="comic-btn comic-btn--go"
              >
                {submitting ? "Recruiting..." : "Recruit Hero"}
              </button>
              {error && <div className="comic-alert-bubble">{error}</div>}
            </form>
          ) : atCap ? (
            <p className="comic-empty" style={{ margin: "4px 2px" }}>
              Hero roster full ({FREE_PROFILE_LIMIT}). Additional Heroes coming soon.
            </p>
          ) : (
            <button
              onClick={() => {
                setError(null);
                setAdding(true);
              }}
              className="comic-btn comic-btn--go"
              style={{ marginTop: "4px" }}
            >
              + Add Profile
            </button>
          )}

          {/* Moved here from the page-level footer per direct user request
              -- these three links now only live inside this expanded panel,
              instead of being visible on every tab regardless of context. */}
          <div style={{ marginTop: "12px", paddingTop: "12px", borderTop: "2px solid #232526", textAlign: "center", fontSize: "11px", color: "#888" }}>
            <a href="/privacy.html" style={{ color: "#888" }}>Privacy</a>
            {" · "}
            <a href="/terms.html" style={{ color: "#888" }}>Terms</a>
            {" · "}
            <DeleteAccount />
          </div>
        </div>
      )}
    </div>
  );
};

export default ProfileSwitcher;
